import { DAY, YEAR_DAYS } from '@/sim/clock';
import { readAt, type LoreSource } from '@/sim/lore';
import { describeLandmark, type Landmark, type LandmarkKind, type Rarity } from '@/world/landmarks';
import { nameHerd } from './attention';
import { extent, regrowth, roofCount } from './lore';

/**
 * THE ARRIVAL — the first ten minutes begin with looking around.
 *
 * Design III §162: *the first ten minutes can define centuries* — smoke, a
 * herd, a waterfall, an old forest; nothing says *this is the main quest*.
 * §163: **do not begin with "find your followers"; begin with "look around".**
 * And §164 asks that when the player awakens several curiosity hooks exist —
 * at least one human, one ecological and one geographic.
 *
 * What the game did: opened the eye over the first living band, said *"722
 * souls, 10 peoples — and none of them know you are here"* for two and a half
 * seconds, and stopped. A beautiful island, five verbs and no reason.
 *
 * What it does now: **the eye wakes, rises a little, and looks around.** For
 * half a minute it turns its gaze, one at a time, onto things the world already
 * knows and had never once used to catch an eye — the smoke of the busiest
 * hearth in sight, a herd that is going somewhere, a hillside that burned
 * sixteen years ago and is scrub now, a camp left to the land within living
 * memory, a waterfall — resting on each while the notice names it in the
 * world's voice. Then it stops, and the eye is the player's, pointed at
 * something worth flying to. **A hand on any control ends it at once**, mid-
 * sentence, and nothing is said about that either.
 *
 * Three constraints, and each is why the module is shaped as it is:
 *
 * **Law 9 / law 15 — looking writes nothing.** Everything here is a read: it
 * takes the same narrow, reader-only `LoreSource` `sim/lore.ts` takes, plus
 * lists the frame already holds, and returns positions and sentences. There
 * is no store, no `mark`, no `emit` in reach of it, and `test/arrival.ts`
 * gathers the hooks and runs the whole tour over a lived-in world and proves
 * the snapshot byte-identical afterwards.
 *
 * **Law 12 — the world is the interface.** No quest log, no marker, no icon
 * floating over the thing. The hook IS the thing — the smoke, the fall, the
 * scar are already drawn — and the gaze is what points. Only what the eye can
 * actually see from where it stands is offered (`canSee` marches the terrain),
 * so the arrival never says *there is a waterfall* about a hillside.
 *
 * **Never an instruction.** Every line states what is there and what has
 * happened to it, in the register `ui/lore.ts` and `ui/lives.ts` use. There is
 * no *go*, no *try*, no *you should*; the vocabulary tripwire in the test
 * holds it there. What the player does with a herd going somewhere is the
 * whole of the game, and telling them would spend it.
 *
 * Pure arithmetic and prose. No three, no DOM, no clock of its own: `main.ts`
 * owns the presence, applies the pose this returns, and decides when a hand
 * has taken over.
 */

/* ── where the eye opens ───────────────────────────────────────────────── */

/**
 * Bands whose homes stand within this of one another share a ground — the
 * same join `tools/standpoint.ts` and `tools/carry.ts` §1 use to find the
 * island's clusters. The shipped island's sixteen camps stand in seven of
 * them, twelve within 60 m of another camp; 300 m is wide enough to take a
 * camp that fission set a few paces off and far narrower than the kilometres
 * between clusters.
 */
export const SHARED_GROUND = 300;

/**
 * THE BAND THE GAME OPENS OVER — a rule, not an id (`DECISIONS.md`, the
 * First Playable lane's rulings, 2026-08-22).
 *
 * It used to be the first living band in the list, which `tools/opening.ts`
 * ranked 14 of 16 by the §162 hooks within 1.5 km, and which stands with one
 * other band on a bare ridge. The rule now: **the living band whose shared
 * ground holds the most souls** — on the shipped century, cluster 1: five
 * bands, 136 souls, one people, six landmarks, three falls and a ruin inside
 * 1.5 km. Two reasons, and the second is the one a regeneration cannot move:
 * a story crosses between bands only on shared ground (`tools/carry.ts` §3 —
 * a cluster is the ceiling of beat 6), and where the most life stands is
 * where a first ten minutes finds the most to notice. Ties break on id, so
 * the answer is a pure function of the world.
 *
 * Written as a rule so that `tools/worldgen.ts` cannot silently strand it:
 * ids are minted by spawn order and a regenerated century has different
 * ones; the biggest ground is whatever it is.
 */
export function openingBand<B extends { id: string; population: number; homeX: number; homeZ: number }>(
  bands: readonly B[],
): B | undefined {
  const live = bands.filter((b) => b.population > 0);
  let best: B | undefined, most = -1;
  for (const b of live) {
    let souls = 0;
    for (const o of live) {
      if (Math.hypot(o.homeX - b.homeX, o.homeZ - b.homeZ) <= SHARED_GROUND) souls += o.population;
    }
    if (souls > most || (souls === most && best && b.id < best.id)) { most = souls; best = b; }
  }
  return best;
}

/** Which of §164's three kinds of curiosity a hook answers. */
export type HookClass = 'human' | 'ecological' | 'geographic';

export type HookKind =
  'hearth' | 'ruin' | 'stirring' | 'herd' | 'burn' | 'landmark' | 'apart';

export interface Hook {
  kind: HookKind;
  cls: HookClass;
  /** Position IS the identity — a hook is a place, never a stored thing. */
  id: string;
  x: number; z: number;
  /**
   * Metres above its own ground the gaze rests at. Smoke rises, a fall has a
   * face, a summit is its own height; a herd and a scar are on the ground.
   */
  lift: number;
  /** Metres across, for how long the eye lingers and how far it can be meant. */
  size: number;
  /** The line, in the world's voice. It states; it never instructs. */
  said: string;
  /** Rank within its kind — higher is the better example of the kind. */
  weight: number;
  /**
   * A finer kind, where one exists — a landmark's own kind. Two glances of one
   * kind are allowed when their sub-kinds differ: a fall and a peak are two
   * things to look at, two peaks are the same thing twice.
   */
  sub?: string;
}

/** A herd as the frame draws it — `render/wildlife.ts`'s `WildlifeView`. */
export interface HerdView {
  species: string; x: number; z: number; count: number; seed: number;
}

/**
 * A cell where a herd is far from equilibrium right now — gathering onto a
 * burn's grass, thinning on a stripped range. `sim.wildlife.stirring()`, the
 * trajectory lane's read; optional here so the arrival runs without it and
 * says more with it.
 */
export interface Stirring {
  sx: number; sz: number; x: number; z: number; species: string;
  kind: 'gathering' | 'thinning' | 'stripped'; strength: number; head: number;
}

/** The last thing the world said about a herd moving — the perception ring. */
export interface RecentEvent {
  type: string; x: number; z: number; time: number; salience: number;
  data?: { species?: string };
}

/** What the arrival reads. Readers only — the type cannot write (law 9). */
export interface ArrivalSource extends LoreSource {
  /**
   * WHAT THE WORLD HAS MADE SIGNIFICANT — `ThingStore.important`, which had no
   * `src/` caller of any kind.
   *
   * It is here rather than anywhere else because the arrival is the one
   * surface whose whole job is *what is worth looking at*, and a promoted
   * thing is the world's own answer to exactly that question. Two rules it
   * obeys, and they are the ones that make it safe:
   *
   *  · **Nothing spawns.** The eye is turned toward something that is already
   *    there and already apart. Observation must not create significance, and
   *    this cannot: it reads a store, and the store is written by consequence.
   *  · **The count is the world's, not the god's.** A `ThingMark` carries no
   *    `godId` — `world/things.ts` cannot say whose hand it was — so a
   *    lightning-split oak is offered on the same terms as a tended one, and
   *    the line never claims a cause (law 10).
   */
  things: LoreSource['things'] & {
    important(now: number): ReadonlyArray<{ x: number; z: number; standing: number }>;
  };
  bands: LoreSource['bands'] & {
    bands(): readonly {
      id: string; population: number; homeX: number; homeZ: number; cultureId?: string;
    }[];
    activeHearths(): readonly string[];
  };
  cultures: { get(id: string): { name: string } | undefined };
}

export interface ArrivalWorld {
  sim: ArrivalSource;
  landmarks: readonly Landmark[];
  herds: readonly HerdView[];
  stirring?: readonly Stirring[];
  events?: readonly RecentEvent[];
  /**
   * The waterfalls the ground actually carries white water on —
   * `render/falls.ts`'s `drawnIds`. 101 of the island's 161 fall landmarks are
   * drawn; the rest are reaches whose whole run is a hillside (drop p10 4.8 m)
   * and there is nothing to see there. Optional; without it every fall is
   * offered.
   */
  drawnFalls?: ReadonlySet<string>;
  now: number;
  /** The terrain, for line of sight — the region's own field is enough. */
  heightAt(x: number, z: number): number;
}

/* ── words ─────────────────────────────────────────────────────────────── */

const YEAR = DAY * YEAR_DAYS;

const SMALL = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight',
  'nine', 'ten', 'eleven', 'twelve'];

/** "sixteen years ago" — words up to a dozen, then the number, the way `ui/lore.ts` dates a place. */
export const yearsAgo = (years: number): string => {
  const n = Math.max(0, Math.round(years));
  if (n === 0) return 'this year';
  if (n === 1) return 'a year ago';
  return `${n < SMALL.length ? SMALL[n] : n} years ago`;
};

/** How many head, without a tally — the same instinct as `lives.ts`'s COUNT. */
export const headOf = (n: number): string =>
  n < 4 ? 'a handful of them'
  : n < 9 ? 'a few head'
  : n < 16 ? 'a dozen or so'
  : n < 30 ? 'a score of them'
  : n < 60 ? 'some forty head'
  : 'a great many';

/** Plural subjects for a stirring — "grazers are gathering", not "a herd at grass are". */
const STIR_NOUN: Record<string, string> = {
  grazer: 'grazers', browser: 'browsers', predator: 'the hunters', scavenger: 'scavengers',
  small_game: 'small game',
};

/** How much a herd of this species is worth a look, per √head. */
const HERD_WORTH: Record<string, number> = {
  grazer: 1.0, browser: 1.0, predator: 2.0, scavenger: 0.5, small_game: 0.2,
};

/**
 * Pixels a thing of `size` metres subtends at `d` metres, on the camera the
 * game is played on (900 px tall, 46° vertical) — `main.ts`'s own `pxOf`,
 * with the frame fixed. A glance is offered only when the thing would be at
 * least `LEGIBLE` pixels: the pick's floor is three (*under about three pixels
 * is not a signal*, `tools/settlement.ts`), and a glance wants more than a
 * signal — it wants a shape the line can be read against.
 */
const pxAt = (size: number, d: number): number =>
  (size * 450) / Math.max(1, d * Math.tan((46 * Math.PI) / 360));
const LEGIBLE = 8;

const NEAR_HOME = 260;      // a camp's own ground: `readAt`'s CAMP_NEAR order
const RUIN = 0.3;           // `render/structures.ts`'s threshold, as `sim/lore.ts` uses it
/**
 * How long ago a camp may have been left and still be worth a glance. The
 * sere's own retire age: at eighty years the world stops distinguishing burned
 * ground from baseline (`world/sere.ts`), and that is the honest horizon for
 * "within living memory" — the shipped island's life table has e0 34 and a
 * tail past seventy-five, and its oldest ruins are 68 years gone.
 */
const LIVING_MEMORY = 80;

/* ── sight ─────────────────────────────────────────────────────────────── */

/**
 * Can the eye at `eye` see a point `lift` metres over the ground at (x, z)?
 * A march along the line against the terrain, at the region's own resolution
 * — the same cheap field the herd distance test uses, because this runs over a
 * few hundred candidates once and the drawn ground is metres off the region's.
 * `slack` forgives the field's own smoothing over a ridge line.
 */
export function canSee(
  heightAt: (x: number, z: number) => number,
  eye: { x: number; y: number; z: number },
  x: number, z: number, lift: number,
  slack = 4,
): boolean {
  const ty = heightAt(x, z) + lift;
  const dx = x - eye.x, dz = z - eye.z, dy = ty - eye.y;
  const len = Math.hypot(dx, dz);
  if (len < 1) return true;
  const steps = Math.max(2, Math.ceil(len / 64));
  // stop short of the target's own cell, which is by definition at the line
  for (let i = 1; i < steps; i++) {
    const f = i / steps;
    if (heightAt(eye.x + dx * f, eye.z + dz * f) > eye.y + dy * f + slack) return false;
  }
  return true;
}

/* ── the hooks ─────────────────────────────────────────────────────────── */

export interface Standpoint {
  /** The camp the eye opens over. Hooks are things OTHER than it. */
  home: { x: number; z: number };
  /** Where the eye will be looking from, once it has risen. */
  eye: { x: number; y: number; z: number };
  /** How far out a hook may be. Fog is 12 km; a fall is 42 px at 3 km. */
  range?: number;
}

const rarityRank: Record<Rarity, number> = { notable: 1, rare: 2, exceptional: 3 };
/** How much of a landmark's kind reads from the air, before its own magnitude. */
const kindReads: Record<LandmarkKind, number> = {
  waterfall: 1.0, peak: 1.0, lake: 0.9, cliff: 0.8, grove: 0.65, pass: 0.5, confluence: 0.5,
};
const kindLift: Record<LandmarkKind, number> = {
  waterfall: 40, peak: 0, lake: 2, cliff: 30, grove: 12, pass: 4, confluence: 2,
};

/**
 * Everything worth a glance from here, all kinds, best of each kind first.
 * `planArrival` chooses among them; this only finds and phrases.
 */
export function gatherHooks(w: ArrivalWorld, at: Standpoint): Hook[] {
  const range = at.range ?? 7000;
  const { home, eye } = at;
  const hooks: Hook[] = [];
  const dist = (x: number, z: number) => Math.hypot(x - home.x, z - home.z);
  const sees = (x: number, z: number, lift: number) => canSee(w.heightAt, eye, x, z, lift);

  /* -- human: smoke, and a camp that people left ------------------------ */
  {
    const bands = w.sim.bands.bands().filter((b) => b.population > 0);
    const structures = w.sim.bands.structures();
    const lit = new Set(w.sim.bands.activeHearths());
    const busiest = bands.reduce((m, b) => Math.max(m, b.population), 0);
    for (const b of bands) {
      const d = dist(b.homeX, b.homeZ);
      if (d < NEAR_HOME || d > range) continue;
      let roofs = 0, smoking = 0;
      for (const s of structures) {
        if (Math.hypot(s.x - b.homeX, s.z - b.homeZ) > NEAR_HOME) continue;
        roofs++;
        if (lit.has(s.id)) smoking++;
      }
      // no fire, no smoke, nothing to look at from here
      if (smoking === 0 || !sees(b.homeX, b.homeZ, 60)) continue;
      const people = b.cultureId ? w.sim.cultures.get(b.cultureId)?.name : undefined;
      const who = people ? `the ${people}` : 'people';
      const said = b.population >= busiest
        ? `smoke, and a good deal of it — ${who} keep the busiest hearth on the island there`
        : `smoke — ${who} keep ${roofCount(roofs)} there`;
      hooks.push({ kind: 'hearth', cls: 'human', id: `hearth:${b.id}`,
                   x: b.homeX, z: b.homeZ, lift: 60, size: 120, said,
                   weight: b.population + (b.population >= busiest ? 1000 : 0) });
    }

    // a camp left to the land: every roof down, dated from the last thing
    // anybody built — `sim/lore.ts`'s own definition, read through it
    const taken = new Set<string>();
    for (const s of structures) {
      if (s.condition >= RUIN) continue;
      const d = dist(s.x, s.z);
      if (d < NEAR_HOME || d > range) continue;
      const cell = `${Math.round(s.x / 200)}:${Math.round(s.z / 200)}`;
      if (taken.has(cell)) continue;
      taken.add(cell);
      const note = readAt(w.sim, s.x, s.z, w.now).world.find((n) => n.kind === 'abandoned');
      if (!note || note.kind !== 'abandoned') continue;
      const years = (w.now - note.at) / YEAR;
      if (years > LIVING_MEMORY || pxAt(80, d) < LEGIBLE || !sees(s.x, s.z, 6)) continue;
      hooks.push({ kind: 'ruin', cls: 'human', id: `ruin:${s.id}`, x: s.x, z: s.z,
                   lift: 6, size: 80,
                   said: `${roofCount(note.roofs)}, left to the land ${yearsAgo(years)} — ` +
                         'nobody has mended anything there since',
                   // the more recently left and the bigger, the better the
                   // story — but a ruin is its clearing, ~60 m of old ground,
                   // and from four kilometres that is a dozen pixels: nearer
                   // wins hard (seen live: the 35-year ruin at 4.4 km was
                   // chosen over the 68-year one at 1.4 km, and was invisible)
                   weight: (LIVING_MEMORY - years + note.roofs) / (1 + (d / 1500) ** 2) });
    }
  }

  /* -- ecological: a herd going somewhere, and ground that burned -------- */
  {
    for (const s of w.stirring ?? []) {
      const d = dist(s.x, s.z);
      if (d > range || !sees(s.x, s.z, 3)) continue;
      const herd = STIR_NOUN[s.species] ?? 'animals';
      const stage = w.sim.sere.stageAt(s.x, s.z, w.now);
      const ground = stage === 'grass' ? ' on the grass of an old burn'
        : stage === 'shrub' ? ' in the scrub of an old burn' : '';
      // 'gathering' near a camp is the herds being drawn in and eaten — the
      // opening band's own hunting grounds are the top four on the shipped
      // file (`tools/trajectory-stir.ts`) — so the line says drawn, and says
      // the burn only when the ground under them is one
      const said = s.kind === 'gathering'
        ? `${herd} are being drawn to that ground${ground} — ${headOf(s.head)}`
        : s.kind === 'thinning'
          ? `${herd} are thinning out there — fewer than there were`
          : `the ground there is grazed bare, and ${herd} are going hungry on it`;
      if (pxAt(300, d) < LEGIBLE) continue;
      hooks.push({ kind: 'stirring', cls: 'ecological', id: `stirring:${s.sx}:${s.sz}:${s.species}`,
                   x: s.x, z: s.z, lift: 3, size: 300, said,
                   // strength, by what the species is worth a look for: small
                   // game is 474 of the 480 stirrings at boot and 3 px at 170 m
                   weight: s.strength * (HERD_WORTH[s.species] ?? 0.5) });
    }

    // the last great migration the ring still holds, and where it started:
    // the event's (x, z) is the ORIGIN cell's centre, and a drawn herd stands
    // inside its cell, so 600 m is "this herd" and the next cell is not
    const moved = (w.events ?? [])
      .filter((e) => e.type === 'wildlife_migrated' && e.salience >= 0.7 &&
                     w.now - e.time < 30 * DAY);
    for (const h of w.herds) {
      const d = dist(h.x, h.z);
      if (d > Math.min(range, 3000) || h.count < 4 || !sees(h.x, h.z, 2)) continue;
      if (pxAt(200, d) < LEGIBLE) continue;
      const herd = nameHerd(h.species).name;
      const going = moved.some((e) =>
        e.data?.species === h.species && Math.hypot(e.x - h.x, e.z - h.z) < 600);
      hooks.push({ kind: 'herd', cls: 'ecological', id: `herd:${h.species}:${h.seed}`,
                   x: h.x, z: h.z, lift: 2, size: 200,
                   said: going ? `${herd} — ${headOf(h.count)}, and they are on the move`
                               : `${herd} — ${headOf(h.count)}`,
                   // what a herd of this kind is worth a look for, before how
                   // many: a hundred rabbits are a hillside, a score of grazers
                   // is a herd, and one predator is the rarest thing on it
                   weight: (HERD_WORTH[h.species] ?? 0.5) * Math.sqrt(h.count) + (going ? 10 : 0) });
    }

    // scars: sample the sere on a lattice and take the biggest patch of one
    // fire that is old enough to have grown something back
    const STEP = 96, R = Math.min(range, 3500);
    const patches = new Map<string, { n: number; sx: number; sz: number; years: number }>();
    for (let z = home.z - R; z <= home.z + R; z += STEP) {
      for (let x = home.x - R; x <= home.x + R; x += STEP) {
        if (Math.hypot(x - home.x, z - home.z) > R) continue;
        const years = w.sim.sere.ageAt(x, z, w.now);
        if (years === null) continue;
        // one fire is one month; `sim/lore.ts`'s SAME_FIRE
        const key = String(Math.round((w.now - years * YEAR) / (30 * DAY)));
        const p = patches.get(key) ?? { n: 0, sx: 0, sz: 0, years };
        p.n++; p.sx += x; p.sz += z;
        patches.set(key, p);
      }
    }
    for (const p of patches.values()) {
      const x = p.sx / p.n, z = p.sz / p.n;
      // the centroid of a ring-shaped scar can be unburned ground; read the
      // sample nearest it that actually burned in that fire
      const note = readAt(w.sim, x, z, w.now).world.find((n) => n.kind === 'burned');
      if (!note || note.kind !== 'burned') continue;
      if (dist(x, z) < NEAR_HOME * 0.5 || !sees(x, z, 2)) continue;
      // only a scar that READS from the air: char, bare ground, grass or scrub
      // where the wood was. `young` (25–80 y) is a stand again to the eye, and
      // a glance that says *fire took that hillside* over unbroken forest is a
      // sentence about a card, not a thing to look at.
      if (note.stage === 'young') continue;
      if (pxAt(Math.max(120, note.spread), dist(x, z)) < LEGIBLE) continue;
      const stage = regrowth[note.stage] ?? 'it is coming back';
      hooks.push({ kind: 'burn', cls: 'ecological', id: `burn:${Math.round(x)}:${Math.round(z)}`,
                   x, z, lift: 2, size: Math.max(120, note.spread),
                   said: `fire took ${extent(note.spread, 'that')} ` +
                         `${yearsAgo(note.years)}, and ${stage}`,
                   // area first, and a scar with a story over a fresh char
                   weight: p.n * (note.years >= 3 ? 1.5 : 1) });
    }
  }

  /* -- what history has set apart from ordinary ------------------------- */
  {
    /**
     * `ThingStore.important()` — the only way to ask the world what it has
     * made significant, and until now called by probes alone.
     *
     * The line says what a witness could say and no more: a particular thing
     * has stopped being ordinary. It names no hand, because the store holds
     * none, and it does not say *blessed* or *cursed* — `standing` is signed,
     * and a lightning-split oak is as much a thing history kept as a tended
     * one, so the two are told apart by what happened to them and never by a
     * verdict on it (law 22, and `world/things.ts`'s own note).
     *
     * `PROMOTE` is 0.55 and `important` already applies it, so anything this
     * returns has genuinely crossed. Weight is how far past the line it is:
     * the strangest thing in sight is the best example of the kind.
     */
    for (const m of w.sim.things.important(w.now)) {
      const d = dist(m.x, m.z);
      if (d < NEAR_HOME * 0.5 || d > range) continue;
      if (!sees(m.x, m.z, 4)) continue;
      // a thing is a thing: if it is not several pixels from here it is a
      // rumour, and the arrival may not point at rumours
      if (pxAt(12, d) < LEGIBLE) continue;
      const wrong = m.standing < 0;
      hooks.push({
        kind: 'apart', cls: 'ecological',
        id: `apart:${Math.round(m.x)}:${Math.round(m.z)}`,
        x: m.x, z: m.z, lift: 4, size: 14,
        said: wrong
          ? 'something happened to that one, and the ground has not forgotten it'
          : 'that one is not an ordinary tree any more, and has not been for a while',
        weight: Math.abs(m.standing),
      });
    }
  }

  /* -- geographic: what the land itself has that is like nowhere else ---- */
  for (const m of w.landmarks) {
    const d = dist(m.x, m.z);
    if (d < NEAR_HOME || d > range) continue;
    const lift = kindLift[m.kind];
    if (m.kind === 'waterfall' && w.drawnFalls && !w.drawnFalls.has(m.id)) continue;
    if (pxAt(m.size, d) < LEGIBLE || !sees(m.x, m.z, lift)) continue;
    const { name, note } = describeLandmark(m);
    hooks.push({ kind: 'landmark', cls: 'geographic', id: m.id, x: m.x, z: m.z, lift,
                 size: m.size, sub: m.kind,
                 said: note ? `${name} — ${note}` : name,
                 // rarity and how much the kind reads from the air, against how
                 // far it is: a notable fall at 600 m is a first flight, a rare
                 // one at 3.5 km is an afternoon, and an exceptional peak carries
                 // its own argument from anywhere. Halves every 2 km.
                 weight: (rarityRank[m.rarity] * kindReads[m.kind]) / (1 + d / 2000) });
  }

  hooks.sort((a, b) => b.weight - a.weight);
  return hooks;
}

/* ── the plan ──────────────────────────────────────────────────────────── */

/** How many things the eye is shown before it is left alone. */
export const GLANCES = 5;

/** Bearing from the eye to a point, in the presence's yaw convention (0 → −z). */
export const bearingTo = (eye: { x: number; z: number }, x: number, z: number): number =>
  Math.atan2(-(x - eye.x), -(z - eye.z));

const wrap = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));

/**
 * Which kind of hook stands for its class when there is a choice, and which
 * kinds fill the remaining glances. Weights rank hooks WITHIN a kind and mean
 * nothing across kinds — a herd's is head, a scar's is cells — so the choice
 * between kinds is made here, once, in words: what the land has that is like
 * nowhere else; a herd that is going somewhere, or failing that a herd, or the
 * ground that burned; smoke before a ruin. §162's own list, in its own order.
 */
const KIND_ORDER: readonly HookKind[] = ['landmark', 'stirring', 'herd', 'hearth', 'burn', 'ruin'];

/**
 * Which hooks, in what order.
 *
 * One of each class first — §164's *at least one human, one ecological and
 * one geographic* — the best of the preferred kind for that class, and then
 * kinds not yet shown, in `KIND_ORDER`, to fill `GLANCES`. Then **ordered by
 * the turn**: from the gaze the eye wakes with, the head goes to the nearer
 * end of the arc the hooks span and sweeps once across to the other, so the
 * looking-around reads as one movement rather than four cuts.
 */
export function planArrival(
  hooks: readonly Hook[], eye: { x: number; z: number }, wakeYaw: number,
  glances = GLANCES,
): Hook[] {
  const chosen: Hook[] = [];
  const shown = new Set<string>();                    // kind, or kind:sub
  const tag = (h: Hook) => (h.sub ? `${h.kind}:${h.sub}` : h.kind);
  // a stirring IS a herd, told with its trajectory: one of the two, not both
  const family = (kind: HookKind) => (kind === 'stirring' ? 'herd' : kind);
  const bestOf = (kind: HookKind) => hooks.find((q) => q.kind === kind && !shown.has(tag(q)));
  const take = (h: Hook | undefined) => { if (h) { chosen.push(h); shown.add(tag(h)); } };
  for (const cls of ['geographic', 'ecological', 'human'] as HookClass[]) {
    for (const kind of KIND_ORDER) {
      const h = bestOf(kind);
      if (h && h.cls === cls) { take(h); break; }
    }
  }
  // then families not yet shown, in the same order — a second landmark only
  // if it is a different KIND of landmark
  for (const kind of KIND_ORDER) {
    if (chosen.length >= glances) break;
    if (!chosen.some((q) => family(q.kind) === family(kind))) take(bestOf(kind));
  }
  for (const kind of KIND_ORDER) {
    if (chosen.length >= glances) break;
    take(bestOf(kind));
  }
  // the turn: signed, from the waking gaze. Go to the nearer end of the arc,
  // then sweep across it once — total turning is that reach plus the span,
  // which is the least any order can manage.
  const turn = new Map(chosen.map((h) => [h, wrap(bearingTo(eye, h.x, h.z) - wakeYaw)]));
  const turns = [...turn.values()];
  const lo = Math.min(0, ...turns), hi = Math.max(0, ...turns);
  const ascending = Math.abs(lo) <= Math.abs(hi);
  return [...chosen].sort((a, b) =>
    ascending ? turn.get(a)! - turn.get(b)! : turn.get(b)! - turn.get(a)!);
}

/* ── the tour ──────────────────────────────────────────────────────────── */

/** Seconds the eye holds the camp before it starts to look around. */
export const WAKE_S = 3.4;
/** Seconds the gaze rests on each thing. Enough to read a line and see the thing. */
export const DWELL_S = 5.5;

export interface Arrival {
  glances: readonly Hook[];
  /** −1 while waking, then the glance in progress; `glances.length` when over. */
  i: number;
  /** Seconds into the current phase. */
  t: number;
  /** Where the eye rises to while it looks around. */
  vantage: { x: number; y: number; z: number };
  done: boolean;
}

export interface ArrivalStep {
  /** What to ease the gaze toward, or null to leave it. */
  look: { x: number; y: number; z: number } | null;
  /** A line to speak — only on the frame a glance begins. */
  say: string | null;
  /** How long the line should stand, in seconds. */
  hold: number;
}

/**
 * Where the eye rises to. The opening standpoint is 139 m over the camp and
 * 310 m off it, gaze on the people; that is the right place to see a camp
 * and the wrong place to see past the ridge. So the arrival lifts to `rise`
 * over the camp's ground and steps back on the same side, keeping the camp
 * under the eye — a god that opens over a place and looks around should end
 * higher over the same place, not somewhere else.
 */
export function vantageOver(
  home: { x: number; z: number }, groundY: number,
  eye: { x: number; z: number }, rise = 420, standoff = 400,
): { x: number; y: number; z: number } {
  const dx = eye.x - home.x, dz = eye.z - home.z;
  const d = Math.hypot(dx, dz) || 1;
  return { x: home.x + (dx / d) * standoff, y: groundY + rise, z: home.z + (dz / d) * standoff };
}

export function beginArrival(
  glances: readonly Hook[], vantage: { x: number; y: number; z: number },
): Arrival {
  return { glances, i: -1, t: 0, vantage, done: glances.length === 0 };
}

/**
 * One frame of the tour. Pure: advances `a` and says what the eye should do.
 * `groundAt` gives each hook its own ground under the lift.
 */
export function stepArrival(
  a: Arrival, dt: number, groundAt: (x: number, z: number) => number,
): ArrivalStep {
  if (a.done) return { look: null, say: null, hold: 0 };
  a.t += dt;
  let say: string | null = null;
  const dwell = a.i < 0 ? WAKE_S : DWELL_S;
  if (a.t >= dwell) {
    a.i++;
    a.t = 0;
    if (a.i >= a.glances.length) {
      a.done = true;
      return { look: null, say: null, hold: 0 };
    }
    say = a.glances[a.i].said;
  }
  if (a.i < 0) return { look: null, say: null, hold: 0 };
  const h = a.glances[a.i];
  return { look: { x: h.x, y: groundAt(h.x, h.z) + h.lift, z: h.z }, say, hold: DWELL_S + 0.8 };
}
